import { BasicType, AdvancedType, IBlockData } from '@bay-email/core';

const DEFAULT_PAGE_WIDTH = 600;

/**
 * How wide a block's content can actually be, in px, given the page width and
 * every container between it and the page.
 *
 * Computed from the template data rather than measured off the canvas on
 * purpose: an oversized image stretches the table it sits in, so measuring
 * would report the overflowed width back as the allowed width and the clamp
 * would never bite.
 */

function toPx(value: unknown, base: number): number | null {
	const raw = String(value ?? '').trim();
	if (!raw) return null;

	const percent = raw.match(/^(\d+(?:\.\d+)?)%$/);
	if (percent) return (Number(percent[1]) / 100) * base;

	const px = raw.match(/^(\d+(?:\.\d+)?)(px)?$/i);
	if (px) return Number(px[1]);

	return null;
}

/**
 * Horizontal padding, from the `padding` shorthand plus any explicit
 * left/right override. Percentage padding is ignored — mjml resolves it
 * against the table, and it is vanishingly rare in these templates.
 */
function horizontalPadding(attributes: Record<string, any> = {}): number {
	const shorthand = String(attributes.padding ?? '')
		.trim()
		.split(/\s+/)
		.filter(Boolean);

	let left = 0;
	let right = 0;

	if (shorthand.length === 1) {
		left = right = toPx(shorthand[0], 0) ?? 0;
	} else if (shorthand.length === 2 || shorthand.length === 3) {
		left = right = toPx(shorthand[1], 0) ?? 0;
	} else if (shorthand.length >= 4) {
		right = toPx(shorthand[1], 0) ?? 0;
		left = toPx(shorthand[3], 0) ?? 0;
	}

	const explicitLeft = toPx(attributes['padding-left'], 0);
	const explicitRight = toPx(attributes['padding-right'], 0);
	if (explicitLeft !== null) left = explicitLeft;
	if (explicitRight !== null) right = explicitRight;

	return left + right;
}

function borderWidth(attributes: Record<string, any> = {}): number {
	const width = toPx(attributes['border-width'], 0);
	if (!width) return 0;
	const style = String(attributes['border-style'] ?? 'solid');
	return style === 'none' ? 0 : width * 2;
}

/**
 * One column's share when `count` columns split `total` equally.
 *
 * Rounding each share independently is wrong in both directions: 600/7 rounds
 * up to 86 and the row overflows by 2px, while flooring loses 5px off the end.
 * Give the remainder to the leading columns instead — every share stays a whole
 * pixel and they sum to exactly `total`.
 */
function equalShare(total: number, count: number, index: number): number {
	const base = Math.floor(total / count);
	const remainder = Math.round(total) - base * count;
	return base + (index < remainder ? 1 : 0);
}

/** Width available *inside* `block`, given the width available to its parent. */
function widthInside(
	block: IBlockData,
	parent: IBlockData | null,
	parentWidth: number,
	index: number,
): number {
	let width = parentWidth;

	if (block.type === BasicType.COLUMN || block.type === AdvancedType.COLUMN) {
		// A column takes its declared width of the section, or an equal share
		// when it has none — which is what mjml does.
		const declared = toPx(block.attributes?.width, parentWidth);
		const siblings = Math.max(1, parent?.children?.length ?? 1);
		width = declared ?? equalShare(parentWidth, siblings, index);
		// A column's border sits outside its width. An image's does not — the
		// page stylesheet puts `.email-block img` in border-box.
		width -= borderWidth(block.attributes);
	}

	return Math.max(0, width - horizontalPadding(block.attributes));
}

/** Indices along `idx`, e.g. `content.children.[0].children.[2]` -> [0, 2]. */
function childIndexes(idx: string): number[] {
	return Array.from(String(idx).matchAll(/children\.\[(\d+)\]/g)).map(match =>
		Number(match[1]),
	);
}

export function getMaxContentWidth(
	values: { content?: IBlockData } | null | undefined,
	idx: string | null | undefined,
): number | null {
	const page = values?.content;
	if (!page || !idx) return null;

	let width = toPx(page.attributes?.width, DEFAULT_PAGE_WIDTH) ?? DEFAULT_PAGE_WIDTH;
	let node: IBlockData = page;

	for (const index of childIndexes(idx)) {
		const child = node.children?.[index];
		if (!child) return null;
		width = widthInside(child, node, width, index);
		node = child;
	}

	// Round, don't truncate. A column declared as `33.33%` of 600 works out to
	// 199.98px, and flooring rendered it 199 — a pixel narrower than the 200 the
	// layout is actually built on.
	return width > 0 ? Math.round(width) : null;
}
